import React from "react";
import { useShow } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Card, Tag, Table, Alert, Statistic, Row, Col, Spin } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import { 
    BankOutlined,
    EditOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../../components/ui";
import { 
    IChartOfAccount, 
    getAccountTypeLabel, 
    getAccountCategoryLabel 
} from "../../../interfaces/accounting";

export const ChartOfAccountsShow: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { queryResult } = useShow({
        resource: "chartofaccounts",
        id,
    });
    const { data, isLoading } = queryResult;
    const record: IChartOfAccount = data?.data;

    const getAccountTypeColor = (accountType: number): string => {
        switch (accountType) {
            case 0: return "blue";    // Asset
            case 1: return "red";     // Liability  
            case 2: return "green";   // Equity
            case 3: return "cyan";    // Revenue
            case 4: return "orange";  // Expense
            default: return "default";
        }
    };

    const getBalanceColor = (balance: number, accountType: number): string => {
        if (balance === 0) return "var(--text-muted)";
        if (accountType === 0 || accountType === 4) {
            return balance > 0 ? "var(--color-success)" : "var(--color-danger)";
        } else {
            return balance > 0 ? "var(--color-primary)" : "var(--color-danger)";
        }
    };

    if (isLoading) {
        return (
            <div className="page-container flex items-center justify-center" style={{ minHeight: 400 }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="page-container">
            <PageHeader
                title={`${record?.accountCode} - ${record?.accountName || 'Account'}`}
                titleIcon={<BankOutlined />}
                breadcrumbs={[
                    { title: "Accounting", href: "/accounting" },
                    { title: "Chart of Accounts", href: "/chartofaccounts" },
                    { title: record?.accountCode || "Details" },
                ]}
                actions={[
                    {
                        label: "Edit",
                        onClick: () => navigate(`/chartofaccounts/edit/${id}`),
                        type: "primary",
                        icon: <EditOutlined />,
                    },
                ]}
            >
                {/* Mini Stat Cards */}
                <div className="mini-stat-cards-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Account Type</div>
                        <Tag color={getAccountTypeColor(record?.accountType)}>
                            {getAccountTypeLabel(record?.accountType)}
                        </Tag>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Current Balance</div>
                        <div className="mini-stat-value" style={{ color: getBalanceColor(record?.currentBalance || 0, record?.accountType) }}>
                            ${(record?.currentBalance || 0).toFixed(2)}
                        </div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Status</div>
                        <div className={`status-badge ${record?.isActive ? 'active' : 'inactive'}`}>
                            {record?.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                            {record?.isActive ? "Active" : "Inactive"}
                        </div>
                    </div>
                    {record?.isSystem && (
                        <div className="mini-stat-card">
                            <div className="mini-stat-label">System</div>
                            <Tag color="orange">System Account</Tag>
                        </div>
                    )}
                </div>
            </PageHeader>

            <div className="space-y-4">
                {/* Account Information Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Account Information</h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Account Code:</span>
                                <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                                    {record?.accountCode}
                                </Tag>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Account Name:</span>
                                <span className="info-value font-semibold">{record?.accountName}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Account Type:</span>
                                <Tag color={getAccountTypeColor(record?.accountType)}>
                                    {getAccountTypeLabel(record?.accountType)}
                                </Tag>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Category:</span>
                                <Tag color="purple">
                                    {getAccountCategoryLabel(record?.accountCategory)}
                                </Tag>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Parent Account:</span>
                                <span className="info-value">
                                    {record?.parentAccountCode ? (
                                        <Tag color="geekblue">{record.parentAccountCode}</Tag>
                                    ) : (
                                        <span className="text-text-muted">Root Account</span>
                                    )}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Normal Balance:</span>
                                <Tag color={record?.accountType === 0 || record?.accountType === 4 ? "blue" : "green"}>
                                    {record?.accountType === 0 || record?.accountType === 4 ? "Debit" : "Credit"}
                                </Tag>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Created Date:</span>
                                <span className="info-value">
                                    <DateField value={record?.createdDate} format="YYYY-MM-DD HH:mm:ss" />
                                </span>
                            </div>
                            {record?.modifiedDate && (
                                <div className="info-item">
                                    <span className="info-label">Modified Date:</span>
                                    <span className="info-value">
                                        <DateField value={record.modifiedDate} format="YYYY-MM-DD HH:mm:ss" />
                                    </span>
                                </div>
                            )}
                            {record?.description && (
                                <div className="info-item full-width">
                                    <span className="info-label">Description:</span>
                                    <span className="info-value">{record.description}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Balance Information Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Balance Information</h3>
                    </div>
                    <div className="info-card-body">
                        <Row gutter={16}>
                            <Col xs={24} sm={8}>
                                <Card size="small" className="text-center">
                                    <Statistic
                                        title="Opening Balance"
                                        value={record?.openingBalance || 0}
                                        precision={2}
                                        prefix="$"
                                        valueStyle={{ fontSize: '18px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={8}>
                                <Card size="small" className="text-center">
                                    <Statistic
                                        title="Current Balance"
                                        value={record?.currentBalance || 0}
                                        precision={2}
                                        prefix="$"
                                        valueStyle={{ 
                                            fontSize: '18px',
                                            color: getBalanceColor(record?.currentBalance || 0, record?.accountType)
                                        }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={8}>
                                <Card size="small" className="text-center">
                                    <Statistic
                                        title="Net Movement"
                                        value={(record?.currentBalance || 0) - (record?.openingBalance || 0)}
                                        precision={2}
                                        prefix="$"
                                        valueStyle={{ 
                                            fontSize: '18px',
                                            color: ((record?.currentBalance || 0) - (record?.openingBalance || 0)) >= 0 ? 'var(--color-success)' : 'var(--color-danger)'
                                        }}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    </div>
                </div>

                {/* Sub Accounts */}
                {record?.subAccounts && record.subAccounts.length > 0 && (
                    <Card title={`Sub Accounts (${record.subAccounts.length})`} className="data-table-card">
                        <Table
                            dataSource={record.subAccounts}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            className="custom-table"
                            scroll={{ x: 600 }}
                        >
                            <Table.Column
                                title="Account Code"
                                dataIndex="accountCode"
                                render={(code: string) => (
                                    <Tag color="blue">{code}</Tag>
                                )}
                            />
                            <Table.Column
                                title="Account Name"
                                dataIndex="accountName"
                            />
                            <Table.Column
                                title="Type"
                                dataIndex="accountType"
                                render={(type: number) => (
                                    <Tag color={getAccountTypeColor(type)}>
                                        {getAccountTypeLabel(type)}
                                    </Tag>
                                )}
                                responsive={['md']}
                            />
                            <Table.Column
                                title="Balance"
                                dataIndex="currentBalance"
                                render={(balance: number, rec: IChartOfAccount) => (
                                    <span style={{ 
                                        color: getBalanceColor(balance, rec.accountType),
                                        fontWeight: 'bold' 
                                    }}>
                                        ${balance?.toFixed(2) || '0.00'}
                                    </span>
                                )}
                                align="right"
                            />
                            <Table.Column
                                title="Status"
                                dataIndex="isActive"
                                render={(isActive: boolean) => (
                                    <Tag color={isActive ? "success" : "default"}>
                                        {isActive ? "Active" : "Inactive"}
                                    </Tag>
                                )}
                                responsive={['lg']}
                            />
                        </Table>
                    </Card>
                )}

                {/* System Account Warning */}
                {record?.isSystem && (
                    <Alert
                        message="System Account"
                        description="This is a system account and cannot be deleted. Modifications should be made carefully."
                        type="warning"
                        showIcon
                    />
                )}
            </div>
        </div>
    );
};
